import { Autocomplete, Chip, FormControl, FormHelperText, Grid, IconButton, Stack, TextField, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { LoadingIndicator } from '@eidsivabredband/react-component-ui-package'
import { EntitiesValidationRequest, EntitiesValidationResult } from '../../interfaces/IAffectedCustomers'
import { enqueueSnackbar } from 'notistack'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { AffectedEntitiesListDialog } from './AffectedEntitiesListDialog'

interface SearchProps {
  onChange: (event: any, value: string[]) => void
  initialEntities?: string[]
  loadingInitial: boolean
  urlPath: string
  title: string
  placeholder?: string
  type: string
  icon: React.ReactNode
  onLoading: (loading: boolean) => void
}

function AddAffectedEntitiesField({ onChange, onLoading, initialEntities, loadingInitial, title, placeholder, urlPath, type, icon }: SearchProps) {
  const { context } = useContext(EbAuthenticatedAppContext)
  const [amountOfTags, setAmountOfTags] = useState(0)
  const [openAddedAffectedEntitiesDialog, setOpenAddedAffectedEntitiesDialog] = useState(false)
  const [openInvalidAffectedEntitiesDialog, setOpenInvalidAffectedEntitiesDialog] = useState(false)
  const [showValidAffectedEntities, setShowValidAffectedEntities] = useState(false)
  const [loadingAffectedEntities, setLoadingAffectedEntities] = useState(false)

  const [affectedEntities, setAffectedEntities] = useState<string[]>(initialEntities ?? [])
  const [invalidAffectedEntities, setInvalidAffectedEntities] = useState<string[]>([])
  const [validatedAffectedEntitiesResult, setValidatedAffectedEntitiesResult] = useState<EntitiesValidationResult>({
    validEntities: [],
    invalidEntities: [],
    alreadyAddedEntities: [],
  })

  const handleShowAllEntities = () => {
    if (affectedEntities && affectedEntities.length <= 1000) {
      if (!showValidAffectedEntities) {
        setAmountOfTags(1000)
        setShowValidAffectedEntities(true)
      } else {
        setAmountOfTags(0)
        setShowValidAffectedEntities(false)
      }
    } else {
      setOpenAddedAffectedEntitiesDialog(true)
      setAmountOfTags(0)
      setShowValidAffectedEntities(false)
    }
  }

  const handleLoading = (loading: boolean) => {
    setLoadingAffectedEntities(loading)
    onLoading(loading)
  }

  const handleEntitiesChange = async (event: any, value: string[]) => {
    setInvalidAffectedEntities([])
    setValidatedAffectedEntitiesResult({
      validEntities: [],
      invalidEntities: [],
      alreadyAddedEntities: [],
    })

    if (value.length < affectedEntities.length) {
      setAffectedEntities(value)
      return
    }

    const entities = event.target.value

    handleLoading(true)

    const processChunksOfEntities = async (chunks: string[]) => {
      if (chunks.length === 0) {
        return
      }

      const newUniqueValues = chunks.splice(0, 3000)

      await addValidatedEntitiesFromBackend(newUniqueValues)
      await new Promise((resolve) => setTimeout(resolve, 0))
      await processChunksOfEntities(chunks)
    }

    const phoneChunks = entities.split(/[\s;,]+/)

    await processChunksOfEntities(phoneChunks)

    handleLoading(false)
  }

  const addValidatedEntitiesFromBackend = async (uniqueEntitiesValues: string[]) => {
    const entities: EntitiesValidationRequest = {
      entities: uniqueEntitiesValues,
      alreadyAddedEntities: affectedEntities,
    }

    var response = await context.restApiClient
      .buildRequest('/api/events/validate' + urlPath)
      .serializeToJsonContent(entities)
      .deserializeResponseToJson<EntitiesValidationResult>()
      .putAsync()

    if (response?.json) {
      const numbers = response.json

      setAffectedEntities((prev) => [...prev, ...numbers.validEntities])
      setInvalidAffectedEntities((prev) => [...prev, ...numbers.invalidEntities])
      return setValidatedAffectedEntitiesResult((prev) => ({
        validEntities: [...prev.validEntities, ...numbers.validEntities],
        invalidEntities: [...prev.invalidEntities, ...numbers.invalidEntities],
        alreadyAddedEntities: [...prev.alreadyAddedEntities, ...numbers.alreadyAddedEntities],
      }))
    }

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Noe gikk galt. Prøv igjen eller kontakt systemansvarlig.</Typography>, { variant: 'warning' })

      handleLoading(false)
      throw new Error()
    }
  }

  useEffect(() => {
    if (validatedAffectedEntitiesResult.invalidEntities.length > 0) {
      enqueueSnackbar(
        <Typography variant="body2">
          Fant {validatedAffectedEntitiesResult.invalidEntities.length} ugyldige {type}.
        </Typography>,
        { variant: 'warning' },
      )
    }

    if (validatedAffectedEntitiesResult.alreadyAddedEntities.length > 0) {
      enqueueSnackbar(
        <Typography variant="body2">
          Fant {validatedAffectedEntitiesResult.alreadyAddedEntities.length} {type} som allerede er lagt til.
        </Typography>,
        { variant: 'warning' },
      )
    }

    setValidatedAffectedEntitiesResult({
      validEntities: [],
      invalidEntities: [],
      alreadyAddedEntities: [],
    })
  }, [!loadingAffectedEntities])

  useEffect(() => {
    setAffectedEntities(initialEntities ?? [])
  }, [initialEntities])

  useEffect(() => {
    onChange(null, affectedEntities)
  }, [affectedEntities])

  return (
    <>
      <AffectedEntitiesListDialog
        title={`${type} lagt til totalt`}
        openDialog={openAddedAffectedEntitiesDialog}
        setOpenDialog={setOpenAddedAffectedEntitiesDialog}
        entities={affectedEntities || ['']}
      />
      <AffectedEntitiesListDialog
        title={`Ugyldige ${type}`}
        openDialog={openInvalidAffectedEntitiesDialog}
        setOpenDialog={setOpenInvalidAffectedEntitiesDialog}
        entities={invalidAffectedEntities || ['']}
      />
      <Stack direction={'column'}>
        <Stack direction={'row'} spacing={1}>
          {icon}
          <Typography variant="body2" sx={{
            alignSelf: "center"
          }}>
            {title}
          </Typography>
          {loadingInitial && <LoadingIndicator size={'xsmall'} />}
        </Stack>
        <Autocomplete
          sx={{
            backgroundColor: 'background.paper',
            width: '100%',
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          }}
          disabled={loadingAffectedEntities}
          multiple
          freeSolo
          limitTags={20}
          options={[]}
          value={affectedEntities}
          filterOptions={(x) => x}
          onChange={(event: any, newValue: string[]) => {
            const allEntities = newValue.map((item) => item.split(/[\s;,]+/)).flat()

            if (newValue.length > 1000) {
              setShowValidAffectedEntities(false)
              setAmountOfTags(0)
            }

            handleEntitiesChange(event, allEntities)
          }}
          renderValue={(value, getItemProps) => (
            <Grid container>
              <Grid>
                {loadingAffectedEntities ? (
                  <Stack direction="row" sx={{
                    alignItems: "center"
                  }}>
                    <LoadingIndicator size={'xsmall'} />
                    <Typography variant="body2">
                      Legger til {type}... Totalt: {value.length}
                    </Typography>
                  </Stack>
                ) : (
                  <Stack direction="column">
                    <Stack direction="row" spacing={1} sx={{
                      alignItems: 'center'
                    }}>
                      <IconButton
                        size="small"
                        onClick={handleShowAllEntities}
                        aria-placeholder={showValidAffectedEntities ? 'Vis færre' : 'Vis alle'}
                        title={`Vis alle ${type}`}
                      >
                        {!showValidAffectedEntities ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <Stack direction="column">
                        <Typography
                          variant="body2"
                          onClick={() => setOpenAddedAffectedEntitiesDialog(true)}
                          sx={{
                            cursor: 'pointer',
                          }}
                        >
                          {`${affectedEntities.length} ${type} lagt til totalt`}
                        </Typography>
                        {invalidAffectedEntities && invalidAffectedEntities.length > 0 && (
                          <Typography
                            variant="caption"
                            onClick={() => setOpenInvalidAffectedEntitiesDialog(true)}
                            sx={{
                              cursor: 'pointer',
                              color: 'error.main',
                            }}
                          >
                            {`${invalidAffectedEntities?.length} ugyldig ${type} (siste søk)`}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </Grid>
              {showValidAffectedEntities && (
                <Grid size={12} sx={{
                  pt: 1
                }}>
                  {value.length <= 1000 &&
                    value.slice(0, amountOfTags).map((option, index) => {
                      const { key, ...tagProps } = getItemProps({ index })

                      return (
                        <Chip
                          key={key}
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            borderColor: 'primary.main',
                          }}
                          label={option}
                          {...tagProps}
                        />
                      )
                    })}
                  {invalidAffectedEntities &&
                    invalidAffectedEntities.length <= 1000 &&
                    invalidAffectedEntities.slice(0, amountOfTags).map((option, index) => (
                      // eslint-disable-next-line react/jsx-key
                      (<Chip
                        variant="outlined"
                        sx={{
                          borderRadius: '8px',
                          borderColor: 'error.main',
                        }}
                        label={option}
                        {...getItemProps({ index })}
                        disabled={true}
                      />)
                    ))}
                </Grid>
              )}
            </Grid>
          )}
          renderInput={(params) => (
            <FormControl fullWidth>
              <TextField
                {...params}
                disabled={loadingAffectedEntities}
                multiline
                variant="outlined"
                placeholder={placeholder}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Backspace' || event.key === 'Delete') {
                    event.stopPropagation()
                  }
                }}
                slotProps={{
                  ...params.slotProps,

                  input: {
                    ...params.slotProps.input,
                    style: {
                      flexWrap: 'wrap-reverse',
                      borderBottomRightRadius: affectedEntities.length === 0 ? 0 : 8,
                      borderBottomLeftRadius: affectedEntities.length === 0 ? 0 : 8,
                    },
                  },

                  htmlInput: {
                    ...params.slotProps.htmlInput,
                    style: {
                      width: '100%',
                      maxHeight: '200px',
                      overflow: 'auto',
                    },
                  },

                  inputLabel: {
                    sx: {
                      fontSize: 'body2.fontSize',
                    },
                  }
                }} />
              {affectedEntities && affectedEntities.length === 0 && (
                <FormHelperText>
                  <Typography variant="caption">Trykk ENTER for å legge til</Typography>
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      </Stack>
    </>
  );
}

export default AddAffectedEntitiesField
